<template>
  <el-dialog v-dialogDrag :title="dialogTitle" :visible.sync="show" width="620px" :before-close="close">
    <div class="group-member-invite">
      <div class="left-box">
        <el-input :placeholder="$t('group.searchUsers')" v-model="searchText" size="small"
          @input="onSearchTextChange">
          <i class="el-icon-search el-input__icon" slot="suffix"> </i>
        </el-input>
        <el-scrollbar v-loading="searchLoading" style="height:400px;">
          <div v-for="friend in friends" :key="friend.id">
            <friend-item @click.native="onSwitchCheck(friend)"
              :menu="false" :friend="friend" size="small">
              <el-checkbox :disabled="friend.disabled" @click.native.stop="" class="checkbox"
                v-model="friend.isCheck" @change="onCheckChange(friend)"></el-checkbox>
            </friend-item>
          </div>
          <div v-if="!searchLoading && friends.length === 0" class="empty-tip">
            {{ searchText ? $t('group.noUserSearchResult') : $t('group.searchUsersTip') }}
          </div>
        </el-scrollbar>
      </div>
      <div class="arrow el-icon-d-arrow-right"></div>
      <div class="right-box">
        <div class="tip">{{ $t('group.checkedUsersCount', { n: checkCount }) }}</div>
        <el-scrollbar style="height:400px;">
          <div v-for="friend in selectedUsers" :key="friend.id">
            <friend-item :friend="friend" size="small"
              @del="onRemoveFriend(friend)" :menu="false">
            </friend-item>
          </div>
        </el-scrollbar>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="close()">{{ $t('common.cancel') }}</el-button>
      <el-button type="primary" :disabled="checkCount === 0 || loading" :loading="loading" @click="onOk()">
        {{ $t('common.ok') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import FriendItem from '../friend/FriendItem.vue';

export default {
  name: "groupMemberInvite",
  components: {
    FriendItem
  },
  data() {
    return {
      show: false,
      loading: false,
      mode: 'invite',
      searchText: "",
      friends: [],
      selectedUsers: [],
      maxSelectSize: 50,
      searchLoading: false,
      searchTimer: null,
      searchRequestId: 0
    }
  },
  methods: {
    openCreate() {
      this.mode = 'create';
      this.initDialog();
    },
    open() {
      this.mode = 'invite';
      this.initDialog();
    },
    initDialog() {
      this.show = true;
      this.loading = false;
      this.searchText = "";
      this.selectedUsers = [];
      this.searchLoading = false;
      this.searchRequestId++;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.loadDefaultUsers();
    },
    close() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.searchRequestId++;
      this.show = false;
    },
    normalizeUser(user) {
      const normalized = JSON.parse(JSON.stringify(user || {}));
      normalized.id = normalized.id || normalized.userId;
      normalized.showNickName = normalized.showNickName || normalized.nickName || normalized.userName || '';
      normalized.nickName = normalized.nickName || normalized.showNickName;
      normalized.headImage = normalized.headImage || normalized.headImageThumb || '';
      normalized.online = normalized.online === true;
      return normalized;
    },
    decorateUser(user) {
      const normalized = this.normalizeUser(user);
      const currentUserId = this.userStore.userInfo && this.userStore.userInfo.id;
      const member = this.members.filter((m) => !m.quit).find((m) => m.userId == normalized.id);
      normalized.disabled = normalized.id == currentUserId || !!member;
      normalized.isCheck = !!member || this.selectedUsers.some(user => user.id == normalized.id);
      return normalized;
    },
    loadDefaultUsers() {
      this.friends = this.friendStore.friends
        .filter(f => !f.deleted)
        .map(f => this.decorateUser(f));
    },
    onSearchTextChange() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      const keyword = this.searchText.trim();
      if (!keyword) {
        this.searchRequestId++;
        this.searchLoading = false;
        this.loadDefaultUsers();
        return;
      }
      const requestId = ++this.searchRequestId;
      // 搜索结果由服务端决定，避免旧的好友列表被继续展示或二次过滤。
      this.friends = [];
      this.searchLoading = true;
      this.searchTimer = setTimeout(() => {
        this.searchUsers(keyword, requestId);
      }, 300);
    },
    async searchUsers(keyword, requestId) {
      this.searchLoading = true;
      try {
        const users = await this.$http({
          url: '/user/search',
          method: 'get',
          params: { name: keyword }
        });
        if (requestId !== this.searchRequestId || !this.show) {
          return;
        }
        this.friends = (users || [])
          .filter(user => user.status !== 1)
          .map(user => this.decorateUser(user));
      } finally {
        if (requestId === this.searchRequestId) {
          this.searchLoading = false;
        }
      }
    },
    onOk() {
      if (this.isCreate) {
        this.onCreateGroup();
      } else {
        this.onInviteFriends();
      }
    },
    onCreateGroup() {
      const userIds = this.selectedUsers.map(user => user.id);
      if (userIds.length === 0) {
        this.$message.warning(this.$t('group.minSelectOneUser'));
        return;
      }
      this.loading = true;
      this.$http({
        url: "/group/new",
        method: 'post',
        data: { userIds }
      }).then((group) => {
        this.$emit("success", group);
        this.close();
      }).finally(() => {
        this.loading = false;
      })
    },
    // 直接按用户 ID 邀请入群，不要求先建立好友关系。
    onInviteFriends() {
      const userIds = this.selectedUsers.map(user => user.id);
      let inviteVO = {
        groupId: this.groupId,
        // 新版后端可直接使用 userIds；保留 friendIds 兼容旧接口。
        userIds,
        friendIds: userIds
      }
      if (userIds.length > 0) {
        this.loading = true;
        this.$http({
          url: "/group/invite",
          method: 'post',
          data: inviteVO
        }).then(() => {
          this.$message.success(this.$t('group.inviteSuccess'));
          this.$emit("reload");
          this.close();
        }).finally(() => {
          this.loading = false;
        })
      }
    },
    onRemoveFriend(friend) {
      friend.isCheck = false;
      this.selectedUsers = this.selectedUsers.filter(user => user.id != friend.id);
    },
    onCheckChange(friend) {
      this.syncSelectedUser(friend);
    },
    syncSelectedUser(friend) {
      if (!friend || friend.disabled) {
        return;
      }
      if (friend.isCheck) {
        if (!this.selectedUsers.some(user => user.id == friend.id)) {
          this.selectedUsers.push(this.normalizeUser(friend));
        }
      } else {
        this.selectedUsers = this.selectedUsers.filter(user => user.id != friend.id);
      }
    },
    onSwitchCheck(friend) {
      if (friend.disabled) {
        return;
      }
      if (!friend.isCheck && this.checkCount >= this.maxSelectSize) {
        this.$message.warning(this.$t('group.maxSelectUsers', { n: this.maxSelectSize }));
        return;
      }
      friend.isCheck = !friend.isCheck;
      this.syncSelectedUser(friend);
    }
  },
  props: {
    groupId: {
      type: Number
    },
    members: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    isCreate() {
      return this.mode === 'create';
    },
    dialogTitle() {
      return this.isCreate ? this.$t('chat.createGroup') : this.$t('group.inviteUsersToGroup');
    },
    checkCount() {
      return this.selectedUsers.length;
    }
  }
}
</script>

<style lang="scss" scoped>
.group-member-invite {
  display: flex;

  .left-box {
    flex: 1;
    overflow: hidden;
    border: var(--im-border);

    .checkbox {
      margin-right: 10px;
    }

    .empty-tip {
      height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--im-text-color-light);
      font-size: var(--im-font-size-small);
    }
  }

  .arrow {
    display: flex;
    align-items: center;
    font-size: 18px;
    padding: 10px;
    color: var(--im-color-primary);
  }

  .right-box {
    flex: 1;
    border: var(--im-border);

    .tip {
      text-align: left;
      height: 32px;
      line-height: 32px;
      text-indent: 10px;
      color: var(--im-text-color-light)
    }
  }
}
</style>
